import { useQuery } from '@tanstack/react-query'
import {
  ActivityFrequencyData,
  ActivityFrequencyMockData,
} from 'src/pages/ContentAnalysisDetail/mockData/activityFrequencyMockData'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { ContentAnalysisApiService } from 'src/services/contentAnalysisApi'

// Raw backend format: each platform is { data: 2D grid, averages: Record }
interface RawPlatformValue {
  averages?: Record<string, number>
  data: number[][]
}

interface RawPlatformGroup {
  [platform: string]: RawPlatformValue
  allPlatform: RawPlatformValue
}

interface RawActivityFrequencyResponse {
  data: {
    engagement: RawPlatformGroup
    posts: RawPlatformGroup
    visibleHours?: string[]
    xAxis?: string[]
    yAxis?: string[]
  }
}

interface TransformedResponse {
  data: {
    engagement: ActivityFrequencyMockData
    posts: ActivityFrequencyMockData
  }
}

const DEFAULT_VISIBLE_HOURS = [
  '8:00',
  '11:00',
  '14:00',
  '17:00',
  '20:00',
  '23:00',
  '03:00',
]

const DEFAULT_X_AXIS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const DEFAULT_Y_AXIS = [
  '8:00',
  '9:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
  '22:00',
  '23:00',
  '00:00',
  '01:00',
  '02:00',
  '03:00',
  '04:00',
  '05:00',
  '06:00',
  '07:00',
]

// Convert raw grid [hourIndex][dayIndex] → triplets [dayIndex, hourIndex, value]
const gridToTriplets = (grid: number[][]): number[][] => {
  const data: number[][] = []
  if (!grid) return data
  grid.forEach((row, hourIndex) => {
    row.forEach((value, dayIndex) => {
      data.push([dayIndex, hourIndex, value])
    })
  })
  return data
}

const transformPlatform = (
  platform: RawPlatformValue,
  xAxis: string[],
  yAxis: string[],
  visibleHours: string[]
): ActivityFrequencyData => ({
  averages: platform.averages,
  data: gridToTriplets(platform.data),
  visibleHours,
  xAxis,
  yAxis,
})

const transformResponse = (
  raw: RawActivityFrequencyResponse
): TransformedResponse => {
  const xAxis = raw.data.xAxis || DEFAULT_X_AXIS
  const yAxis = raw.data.yAxis || DEFAULT_Y_AXIS
  const visibleHours = raw.data.visibleHours || DEFAULT_VISIBLE_HOURS

  const transformGroup = (
    group: RawPlatformGroup
  ): ActivityFrequencyMockData => {
    const result: ActivityFrequencyMockData = {
      allPlatform: transformPlatform(
        group.allPlatform,
        xAxis,
        yAxis,
        visibleHours
      ),
    }
    Object.keys(group).forEach((key) => {
      if (key !== 'allPlatform') {
        result[key] = transformPlatform(group[key], xAxis, yAxis, visibleHours)
      }
    })
    return result
  }

  return {
    data: {
      engagement: transformGroup(raw.data.engagement),
      posts: transformGroup(raw.data.posts),
    },
  }
}

interface UseGetContentAnalysisActivityFrequencyQueryParams {
  fromDate?: string
  id: string
  toDate?: string
}

export const useGetContentAnalysisActivityFrequencyQuery = ({
  fromDate,
  id,
  toDate,
}: UseGetContentAnalysisActivityFrequencyQueryParams) => {
  return useQuery({
    enabled: !!id,
    queryFn: () =>
      ContentAnalysisApiService.getActivityFrequency<RawActivityFrequencyResponse>(
        id,
        { fromDate, toDate }
      ),
    queryKey: [
      QueryKey.GET_CONTENT_ANALYSIS_ACTIVITY_FREQUENCY,
      id,
      fromDate,
      toDate,
    ],
    select: transformResponse,
  })
}
